import { useEffect, useRef, useState } from 'react'
import usePrevious from 'hooks/usePrevious'

const META_PATH = '/meta.json'

const getLatestVersion = async () => {
  return fetch(META_PATH, {
    headers: {
      Accept: 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        console.error(`Network request for latest version was not OK: ${response.status}`)
        return { version: null }
      }

      return response.json()
    })
    .then(({ version }) => {
      if (version) return version
      console.error('Meta request did not contain a version.')
      return null
    })
    .catch(error => {
      console.error(error)
      return null
    })
}

const DEFAULT_POLLING_INTERVAL = 5000

interface Config {
  pollingInterval?: number
  enabled?: boolean
  initialVersion?: string
}

export function useVersionPolling(callback: () => void, config: Config) {
  const [version, setVersion] = useState(config.initialVersion)
  const versionTimer = useRef<number>()
  const previousVersion = usePrevious(version)
  const pollingInterval = config?.pollingInterval ?? DEFAULT_POLLING_INTERVAL
  const enabled = config?.enabled ?? true

  useEffect(() => {
    if (enabled) {
      versionTimer.current = window.setInterval(() => {
        getLatestVersion().then((newVersion: string | null) => {
          if (newVersion) setVersion(newVersion)
        })
      }, pollingInterval)
    }

    return () => {
      if (versionTimer.current) {
        window.clearInterval(versionTimer.current)
      }
    }
  }, [versionTimer, setVersion, pollingInterval, enabled])

  useEffect(() => {
    if (previousVersion !== version) callback()
  }, [version, previousVersion, callback])
}

export default useVersionPolling
